import {HttpClient} from '@angular/common/http';
import {inject, Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {AuditSafeComment, IAuditSafeComment, IBusinessPartner, IJob, Job} from '@service-and-repairs/awpintegrationlib';
import {Observable} from 'rxjs';
import {UserData} from '../../user/models/user-data';
import {UserService} from '../../user/services/user.service';
import {ServiceCase} from '../models/service-case';
import {ServiceCaseUpdateError} from '../models/service-case-update-error';

@Injectable({
  providedIn: 'root'
})
export class ServiceCaseService {
  private readonly httpClient: HttpClient = inject(HttpClient);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly userService: UserService = inject(UserService);

  private static getServiceCasesByVinParams(vin: string, user: UserData): URLSearchParams {
    const queryParams: string[][] = [
      [
        'assigned_distribution_partner_number',
        user.getBusinessPartner()?.getDistributionPartnerNumber()
      ],
      ['vin_short', vin.length === 7 ? vin : undefined],
      ['vin_long', vin.length === 17 ? vin : undefined]
    ].filter((param: string[]) => param[1]);

    return new URLSearchParams(queryParams);
  }

  private static removeExcessServiceCasePropertiesForUpdatingMetadata(serviceCase: ServiceCase): ServiceCase {
    const clone: ServiceCase = ServiceCase.fromPlainObject(serviceCase);
    clone.getJobs().splice(0);
    clone.setVehicle(null);
    clone.setCampaigns(undefined);
    clone.setServiceContracts(undefined);
    clone.setWarrantyRestrictions(undefined);
    clone.setWarnings([]);
    clone.setCustomers(undefined);
    return clone;
  }

  /**
   * Persists the service case in the backend. The external id (set by the backend service) is set in the service case.
   * @param newCase The service case to be persisted in the backend
   */
  createServiceCase(newCase: ServiceCase): Promise<ServiceCase> {
    newCase.setCurrencyCode(this.getCurrencyCode());
    return new Promise<ServiceCase>((resolve, reject): void => {
      this.httpCreateServiceCase(newCase).subscribe({
        next: (response: any): void => resolve(ServiceCase.fromPlainObject(response)),
        error: (): void => reject(new Error(this.translate.instant('case.notificationSaveError')))
      });
    });
  }

  /**
   * Updates the service case in the backend.
   * @param serviceCase The service case to be updated in the backend
   */
  updateMetadataInServiceCase(serviceCase: ServiceCase): Promise<ServiceCase> {
    serviceCase.setCurrencyCode(this.getCurrencyCode());
    return new Promise<ServiceCase>((resolve, reject): void => {
      this.httpUpdateMetadata(serviceCase).subscribe({
        next: (response: any): void => resolve(ServiceCase.fromPlainObject(response.data)),
        error: (response: any): void => reject(ServiceCaseUpdateError.fromHttpErrorResponse(response))
      });
    });
  }

  /**
   Updates the given job in a service case in the backend.
   @param serviceCase service case
   @param job job to update
   */
  updateJobInServiceCase(serviceCase: ServiceCase, job: IJob): Promise<IJob> {
    return new Promise<IJob>((resolve, reject): void => {
      this.httpUpdateJob(serviceCase, job)
        .subscribe({
          next: (response: any): void => resolve(Job.constructFromPlainObject(response.data)),
          error: (response: any): void => reject(ServiceCaseUpdateError.fromHttpErrorResponse(response))
        });
    });
  }

  /**
   * Deletes the given job in a service case in the backend.
   * @param serviceCase service case
   * @param jobId external if of job to delete
   */
  deleteJobInServiceCase(serviceCase: ServiceCase, jobId: string): Promise<void> {
    return new Promise<void>((resolve, reject): void => {
      this.httpDeleteJob(serviceCase.getExternalId(), jobId).subscribe({
        next: (): void => resolve(),
        error: (response: any): void => reject(ServiceCaseUpdateError.fromHttpErrorResponse(response))
      });
    });
  }

  /**
   * Retrieves a service case from the backend with the external id.
   * @param externalId External id of the service case to look up
   */
  getServiceCase(externalId: string): Promise<ServiceCase> {
    return new Promise<ServiceCase>((resolve, reject): void => {
      this.httpGetServiceCase(externalId).subscribe({
        next: (response: any): void => {
          const serviceCase: ServiceCase = ServiceCase.fromPlainObject(response);
          this.setAssignedBusinessPartnerIdIfMissing(serviceCase);
          serviceCase.setCurrencyCode(this.getCurrencyCode());
          resolve(serviceCase);
        },
        error: (response: any): void => reject(this.getLoadingFailedReason(response.statusCode))
      });
    });
  }

  /**
   * Retrieves service cases for a VIN modified within the last 42 days from the backend. In B2D context the service
   * cases are limited to the dealer outlet's service cases.
   * cases.
   * @param vin vehicle identification number
   */
  getServiceCasesByVin(vin: string): Promise<ServiceCase[]> {
    return new Promise<ServiceCase[]>((resolve, reject): void => {
      this.httpGetServiceCasesByVin(vin).subscribe({
        next: (response: any): void => {
          const serviceCases: ServiceCase[] = response.data.map((plainServiceCase: any): ServiceCase => {
            const serviceCase: ServiceCase = ServiceCase.fromPlainObject(plainServiceCase);
            this.setAssignedBusinessPartnerIdIfMissing(serviceCase);
            serviceCase.setCurrencyCode(this.getCurrencyCode());

            // ServiceCase.fromPlainObject clears demandCategories as the API does not return jobs.
            serviceCase.setDemandCategories(plainServiceCase.demandCategories);
            return serviceCase;
          });
          resolve(serviceCases);
        },
        error: ((reason: any): void => {
          console.error(this.translate.instant('case.notificationLoadError') + ':' + reason);
          reject(new Error(this.translate.instant('case.notificationLoadError')));
        })
      });
    });
  }

  createAuditSafeComment(comment: IAuditSafeComment): Promise<IAuditSafeComment> {
    return new Promise<IAuditSafeComment>((resolve, reject): void => {
      this.httpClient.post('/api/service-case/v3/audit-safe-comments', comment)
        .subscribe({
          next: (response: any): void => resolve(AuditSafeComment.constructFromPlainObject(response.data)),
          error: (response: any): void => reject(response.errors.at(0))
        });
    });
  }

  private getLoadingFailedReason(statusCode: any): Error {
    let reason: string;
    if (statusCode?.valueOf() === 403) {
      reason = this.translate.instant('case.notificationLoadErrorNotAllowed');
    } else if (statusCode?.valueOf() === 404) {
      reason = this.translate.instant('case.notificationLoadErrorDeleted');
    } else {
      reason = this.translate.instant('case.notificationLoadError');
    }
    return new Error(reason);
  }

  // TODO: Remove once all service cases have a business partner ID (e.g. by applying an SQL script at deployment time).
  private setAssignedBusinessPartnerIdIfMissing(serviceCase: ServiceCase): void {
    if (!serviceCase.getAssignedBusinessPartnerId() || serviceCase.getAssignedBusinessPartnerId() === '') {
      const businessPartnerId: string = this.getBusinessPartnerIdForOutlet(
        serviceCase.getAssignedDistributionPartnerNumber(),
        serviceCase.getAssignedOutletNumber()
      );
      serviceCase.setAssignedBusinessPartnerId(businessPartnerId);
    }
  }

  private getBusinessPartnerIdForOutlet(distributionPartnerNumber: string, outletNumber: string): string {
    const userData: UserData = this.userService.getCurrentUser();

    // Use current business partner ID as default
    let businessPartnerId: string = userData?.getBusinessPartner()?.getBusinessPartnerId();

    // Try to find business partner ID for existing dpNo and outletNo
    userData?.getBusinessPartners().forEach((partner: IBusinessPartner): void => {
      if (partner.getDistributionPartnerNumber() === distributionPartnerNumber
        && partner.getOutletNumber() === outletNumber) {
        businessPartnerId = partner.getBusinessPartnerId();
      }
    });
    return businessPartnerId;
  }

  private getCurrencyCode(): string | undefined {
    const countryCode: string = this.userService.getCurrentUser()?.getB2XCountryCode();
    const currencies: Map<string, string> = new Map<string, string>([
      ['AU', 'AUD'],
      ['BE', 'EUR'],
      ['BR', 'BRL'],
      ['BG', 'EUR'],
      ['CN', 'CNY'],
      ['DE', 'EUR'],
      ['FI', 'EUR'],
      ['FR', 'EUR'],
      ['GR', 'EUR'],
      ['IN', 'INR'],
      ['ID', 'IDR'],
      ['IE', 'EUR'],
      ['IT', 'EUR'],
      ['JP', 'JPY'],
      ['CA', 'CAD'],
      ['KR', 'KRW'],
      ['MY', 'MYR'],
      ['MX', 'MXN'],
      ['NL', 'EUR'],
      ['NO', 'NOK'],
      ['AT', 'EUR'],
      ['PA', 'USD'],
      ['PL', 'PLN'],
      ['PT', 'EUR'],
      ['RO', 'RON'],
      ['RU', 'RUB'],
      ['SE', 'SEK'],
      ['SK', 'EUR'],
      ['CH', 'CHF'],
      ['SI', 'EUR'],
      ['ES', 'EUR'],
      ['ZA', 'ZAR'],
      ['TH', 'THB'],
      ['CZ', 'CZK'],
      ['HU', 'HUF'],
      ['GB', 'GBP'],
      ['US', 'USD']
    ]);
    return currencies.get(countryCode) || null;
  }

  /**
   * Gets the service case with the given external id.
   * @param id external id
   */
  private httpGetServiceCase(id: string): Observable<Object> {
    return this.httpClient.get('/api/service-case/v1/' + id, {withCredentials: true});
  }

  /**
   * Gets all service cases that map the given VIN. In B2D context the service cases are limited to the user's outlet.
   * @param vin vehicle identification number
   */
  private httpGetServiceCasesByVin(vin: string): Observable<Object> {
    const params: URLSearchParams = ServiceCaseService.getServiceCasesByVinParams(
      vin,
      this.userService.getCurrentUser()
    );
    return this.httpClient.get('/api/service-case/v3?' + params, {withCredentials: true});
  }

  /**
   * Persists the given service case in the backend.
   * @param serviceCase service case to persist
   */
  private httpCreateServiceCase(serviceCase: ServiceCase): Observable<Object> {
    const serviceCaseForBackend: ServiceCase = ServiceCaseService.removeExcessServiceCasePropertiesForUpdatingMetadata(
      serviceCase);
    return this.httpClient.post('/api/service-case/v1', serviceCaseForBackend);
  }

  /**
   * Updates the given service case in the backend.
   * @param serviceCase service case to update
   */
  private httpUpdateMetadata(serviceCase: ServiceCase): Observable<Object> {
    const serviceCaseForBackend: ServiceCase = ServiceCaseService.removeExcessServiceCasePropertiesForUpdatingMetadata(
      serviceCase);
    return this.httpClient.put('/api/service-case/v3/metadata', serviceCaseForBackend);
  }

  /**
   * Updates the given job in a service case in the backend.
   * @param serviceCase  service case
   * @param job job to update
   */
  private httpUpdateJob(serviceCase: ServiceCase, job: IJob): Observable<Object> {
    const isConsultationJob: boolean = serviceCase.getConsultationJobs()
      .some((j: IJob): boolean => j.getExternalId() === job.getExternalId());
    return this.httpClient.put(
      '/api/service-case/v3/' + serviceCase.getExternalId() + '/jobs?isConsultationJob=' + isConsultationJob,
      job
    );
  }

  /**
   * Deletes the given job in a service case in the backend.
   * @param serviceCaseId external id of service case
   * @param jobId external if of job to delete
   */
  private httpDeleteJob(serviceCaseId: string, jobId: string): Observable<Object> {
    return this.httpClient.delete('/api/service-case/v3/' + serviceCaseId + '/jobs/' + jobId);
  }
}
